/**
 * Onboarding · intra-module navigation components (RW-1024).
 *
 * The connective tissue the module was missing: the global RPShell rail gets
 * you INTO Onboarding; these move you BETWEEN its ~16 sub-views.
 *
 *   • OnboardingBar  — horizontal tab strip for the new hire (3 calm stops).
 *   • OnboardingRail — grouped, collapsible (212 ↔ 64px) left rail for staff.
 *
 * Both are presentational: the destination set is passed in from the single
 * `onboardingNav.js` config. Visual spec ports the design's SNVBar / SNVRail
 * (rollout/rp-onboarding-subnav.jsx) onto the live `obKit` warm tokens.
 *
 * Keyboard-first (CLAUDE.md UX rule + RW-791): every destination is a real
 * <button>, so Tab focuses and Enter/Space activates with no extra wiring;
 * active item carries aria-current="page". Rail collapse persists per user via
 * localStorage. The operator (real logged-in user) drives the rail header role
 * label — never a hard-coded persona (RW-1024 persona-scoping fix).
 */
import React from 'react';
import { OB, OBIcon } from './obKit';
import useIsMobile from '../../hooks/useIsMobile';

const RAIL_STORAGE_KEY = 'reve.onboarding.rail.collapsed';
const NARROW_QUERY = '(max-width: 767px)'; // RW-3560: phones + small tablets

const ROLE_LABELS = {
  super_admin: 'Admin', admin: 'Admin', agent_services: 'Agent Services',
  tc: 'Transaction Coord.', trainer: 'Trainer', broker: 'Managing Broker',
  office_coordinator: 'Office Coordinator', marketing: 'Marketing',
  relo_services: 'Relocation', agent: 'Agent',
};
function operatorRoleLabel(user) {
  const raw = user && (user.roles != null ? user.roles : user.role);
  const first = Array.isArray(raw) ? raw[0] : raw;
  return ROLE_LABELS[first] || 'Agent Services';
}

/* ═════════════════ A · Horizontal bar (new hire) ═════════════════ */
// RW-786: `mobile` compacts the bar for phone widths — the "My Onboarding"
// label collapses to its icon chip (title-attributed) and the horizontal
// padding tightens, so the scrollable tab strip keeps usable room.
export function OnboardingBar({ items = [], activeId, onSelect, label = 'My Onboarding', mobile = false }) {
  return (
    <nav aria-label="Onboarding" style={{
      display: 'flex', alignItems: 'stretch', gap: 0, background: '#fff',
      borderBottom: `1px solid ${OB.line}`, padding: mobile ? '0 10px' : '0 22px', height: 50,
      flexShrink: 0, fontFamily: OB.font,
    }}>
      <div title={mobile ? label : undefined} style={{
        display: 'flex', alignItems: 'center', gap: 9, paddingRight: mobile ? 10 : 20,
        marginRight: 4, borderRight: `1px solid ${OB.line2}`,
      }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, background: OB.coralSoft, display: 'grid', placeItems: 'center' }}>
          <OBIcon name="sparkles" size={13} color={OB.coralDark} />
        </div>
        {!mobile && (
          <span style={{ fontFamily: OB.serif, fontSize: 15, fontWeight: 500, color: OB.ink, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{label}</span>
        )}
      </div>

      <div role="tablist" aria-label="Onboarding views" style={{ display: 'flex', alignItems: 'stretch', flex: 1, minWidth: 0, overflowX: 'auto' }}>
        {items.map((it) => {
          const on = it.id === activeId;
          return (
            <button
              key={it.id}
              type="button"
              role="tab"
              aria-selected={on}
              aria-current={on ? 'page' : undefined}
              onClick={() => onSelect && onSelect(it)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '0 13px',
                cursor: 'pointer', position: 'relative', background: 'transparent',
                border: 'none', borderBottom: on ? `2px solid ${OB.coral}` : '2px solid transparent',
                color: on ? OB.ink : OB.muted, fontWeight: on ? 600 : 500,
                fontSize: 13, fontFamily: OB.font, whiteSpace: 'nowrap',
              }}
            >
              <OBIcon name={it.icon} size={15} color={on ? OB.coralDark : OB.mute2} />
              <span>{it.name}</span>
              {/* RW-1711: the bar's badge branch was removed DELIBERATELY.
                  HIRE_NAV never carried a `badge` field, so this was
                  unreachable render code — and after RW-1237's nav-honesty
                  relabel ('tasks' opens the Agent Info FORM, not a task
                  list) the design's "My Tasks · 3" count has no honest stop
                  to live on. The hire's open-item counts render inside the
                  checklist itself (deck ab-07's inline phase counts). If a
                  bar badge ever returns, it returns through HIRE_NAV data,
                  not a resurrected branch here. */}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ═════════════════ B · Secondary rail (staff, collapsible) ═════════════════ */
export function OnboardingRail({
  groups = [], activeId, onSelect, operator, badges = {},
  label = 'Onboarding', storageKey = RAIL_STORAGE_KEY,
}) {
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return window.localStorage.getItem(storageKey) === '1'; } catch (e) { return false; }
  });
  const toggle = React.useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { window.localStorage.setItem(storageKey, next ? '1' : '0'); } catch (e) { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  // RW-3560: on a phone the 212px rail squeezed the content until task titles
  // clipped to one letter. Below 768px it starts in its 64px icon form whatever
  // the saved preference says; Expand there opens it for this visit only and
  // picking a destination folds it again, so the desktop preference is untouched.
  const narrow = useIsMobile(NARROW_QUERY);
  const [narrowOpen, setNarrowOpen] = React.useState(false);
  const isCollapsed = narrow ? !narrowOpen : collapsed;
  const onToggle = narrow ? () => setNarrowOpen((o) => !o) : toggle;
  const pick = (it) => {
    if (narrow) setNarrowOpen(false);
    if (onSelect) onSelect(it);
  };

  const W = isCollapsed ? 64 : 212;
  const roleLabel = operatorRoleLabel(operator);

  return (
    <nav aria-label="Onboarding sections" style={{
      width: W, flexShrink: 0, background: OB.panelDeep, borderRight: `1px solid ${OB.line}`,
      padding: isCollapsed ? '20px 10px' : '20px 12px', display: 'flex', flexDirection: 'column',
      fontFamily: OB.font, alignSelf: 'stretch', minHeight: 'calc(100vh - 160px)',
      transition: 'width .16s ease', overflow: 'hidden',
    }}>
      {/* Header — operator-aware (persona-scoping fix) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, paddingBottom: 14, marginBottom: 14,
        borderBottom: `1px solid ${OB.line}`, justifyContent: isCollapsed ? 'center' : 'flex-start',
      }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: OB.coralSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <OBIcon name="sparkles" size={15} color={OB.coralDark} />
        </div>
        {!isCollapsed && (
          <div style={{ lineHeight: 1.05, flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: OB.serif, fontSize: 16, fontWeight: 500, color: OB.ink, letterSpacing: '-0.01em' }}>{label}</div>
            <div title={roleLabel} style={{ fontSize: 9.5, color: OB.muted, letterSpacing: '0.16em', fontWeight: 700, marginTop: 2, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{roleLabel}</div>
          </div>
        )}
      </div>

      {/* Destinations */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: isCollapsed ? 6 : 16 }}>
        {groups.map((g, gi) => (
          <div key={g.label || gi} style={{ display: 'flex', flexDirection: 'column', gap: isCollapsed ? 4 : 2 }}>
            {g.label && (isCollapsed
              ? (gi > 0 ? <div style={{ height: 1, background: OB.line, margin: '2px 8px' }} /> : null)
              : <div style={{ fontSize: 9, color: OB.mute2, letterSpacing: '0.16em', fontWeight: 700, padding: '0 8px 6px', textTransform: 'uppercase' }}>{g.label}</div>
            )}
            {g.items.map((it) => {
              const on = it.id === activeId;
              const badge = badges[it.id];
              return (
                <button
                  key={it.id}
                  type="button"
                  aria-current={on ? 'page' : undefined}
                  title={isCollapsed ? it.name : undefined}
                  onClick={() => pick(it)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 10,
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    padding: isCollapsed ? '9px 0' : '8px 10px', borderRadius: 8, cursor: 'pointer',
                    position: 'relative', border: 'none', textAlign: 'left', width: '100%',
                    background: on ? '#fff' : 'transparent',
                    boxShadow: on ? '0 1px 2px rgba(40,30,20,.05)' : 'none',
                    color: on ? OB.ink : OB.textSoft, fontWeight: on ? 600 : 500,
                    fontSize: 13, fontFamily: OB.font,
                  }}
                >
                  {on && <div style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 2.5, background: OB.coral, borderRadius: 2 }} />}
                  <div style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
                    <OBIcon name={it.icon} size={16} color={on ? OB.coralDark : OB.mute2} />
                    {isCollapsed && badge != null && (
                      <span style={{ position: 'absolute', top: -5, right: -7, minWidth: 8, height: 8, borderRadius: 5, background: OB.coral, border: `1.5px solid ${OB.panelDeep}` }} />
                    )}
                  </div>
                  {!isCollapsed && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</span>}
                  {!isCollapsed && badge != null && (
                    <span style={{ minWidth: 16, height: 16, padding: '0 5px', borderRadius: 8, background: on ? OB.coral : OB.line, color: on ? '#fff' : OB.muted, fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center' }}>{badge}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Collapse toggle */}
      <div style={{ paddingTop: 12, marginTop: 8, borderTop: `1px solid ${OB.line}` }}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand onboarding navigation' : 'Collapse onboarding navigation'}
          aria-expanded={!isCollapsed}
          title={isCollapsed ? 'Expand' : 'Collapse'}
          style={{
            display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 9,
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            padding: isCollapsed ? '8px 0' : '8px 10px', borderRadius: 8, cursor: 'pointer',
            border: 'none', background: 'transparent', width: '100%',
            color: OB.muted, fontSize: 12, fontWeight: 600, fontFamily: OB.font,
          }}
        >
          <OBIcon name="chevR" size={15} color={OB.muted} style={{ transform: isCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform .16s ease' }} />
          {!isCollapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  );
}
